import {
  Flex,
  Divider,
  Text,
  Icon,
  useTheme,
  Tooltip,
  Box,
  GridItem,
} from '@chakra-ui/react';
import {
  InfoIcon,
  SortAsscending,
  SortDescending,
  SortIcon,
} from '../../../../icons';
import { useContributorTableStore } from '../../../../hooks/useContributorTableStore';
import { useDashboardStore } from '../../../../hooks/useDashboardStore';
import { useContributorTableGql } from '../../graphql';
import TrendCell from './TrendCell';
import MetricLoadingCell from '../LoadingCell/MetricLoadingCell';
import { useEffect, useState } from 'react';
import { DATA_POINT_KEYS } from '../../../../shared/utils';
import { MetricFilterState } from '../../types';
import type {
  MetricTooltip,
  ContributorMetricRow,
} from '../../types/contributorTableTypes';

interface MetricState {
  metricTitle: string;
  metricKey: string;
  sequence: number;
}
type Props = {
  contributors: string[];
  metricTooltip?: MetricTooltip;
  title: string;
  metricKey: string;
  currentPage: number;
  compareTo: string;
  orderColumn: string;
  initialOrderBy: string;
  searchTerm: string;
  languageState: string[];
  activityFilter: any;
  repoState: string[];
  jobRoleState: string[];
  setOrderColumn: (orderColumn: string) => void;
  setOrderByUrl: (orderBy: string) => void;
  setTotalPages: (totalPages: number) => void;
  setCurrentPage: (currentPage: number) => void;
  scrollToTop: () => void;
  loading: boolean;
  selectedMetrics: MetricState[];
  rowHeight?: string;
  startDate?: string;
  endDate?: string;
  teamState: string[];
  metricList: MetricTooltip[];
  metricFilterState: MetricFilterState[];
  showContributorPercentiles?: boolean;
  disableSort?: boolean;
  /** When set, render header + body as GridItems for a parent CSS Grid (Contributor Report). */
  gridLayout?: {
    columnStart: number;
    /** One table body row per contributor email, same order as engineering stats rows. */
    rowContributors: string[];
  };
  /** When set, passed to TrendCell for metric sidebar `teamId` (e.g. Engineer Stats local team). */
  sidebarTeamId?: string;
};

// This component is used to display the metric column in the contributor table all the columns added through configure column are rendered using this component

const MetricCol = ({
  metricTooltip,
  contributors,
  title,
  metricKey,
  searchTerm,
  repoState,
  languageState,
  jobRoleState,
  activityFilter,
  currentPage,
  compareTo,
  orderColumn,
  selectedMetrics,
  setOrderColumn,
  setTotalPages,
  setCurrentPage,
  scrollToTop,
  initialOrderBy,
  setOrderByUrl,
  loading,
  rowHeight,
  startDate,
  endDate,
  teamState,
  metricList,
  metricFilterState,
  showContributorPercentiles,
  disableSort = false,
  sidebarTeamId,
  gridLayout,
}: Props) => {
  const { colors } = useTheme();
  const { selected, selectedTeam } = useDashboardStore();
  const {
    setSelectedContributors,
    selectedContributors,
    setFilteredContributors,
    setIsContributorsEmpty,
    filteredContributors,
  } = useContributorTableStore();
  const [orderBy, setOrderBy] = useState<string>(initialOrderBy);
  const [isHover, setIsHover] = useState(false);

  const isSortApplied = disableSort ? false : !!orderColumn;
  const isSortedColumn = disableSort ? false : orderColumn === metricKey;

  const advancedFilter = metricFilterState?.[0] || ({} as MetricFilterState);
  const isAdvanceFilterApplied = !!advancedFilter.metricName;
  const isAdvanceFilterColumn = advancedFilter.metricName === metricKey;
  const isAdvanceFilterColumnWithoutSort =
    isAdvanceFilterColumn && !isSortApplied;
  const isAdvanceFilterColumnWithSort = isAdvanceFilterColumn && isSortApplied;
  const { data, isLoading } = useContributorTableGql(
    {
      searchTerm: searchTerm.length > 2 ? searchTerm.trim() : '',
      showContributorPercentiles: [
        'OVERDUE_TICKETS',
        'BLOCKED_TICKETS',
        'ACTIVE_WORKLOAD',
        'TOTAL_WORKLOAD',
        'TIME_TO_ZERO',
      ].includes(metricKey)
        ? false
        : showContributorPercentiles || false,
      repoState,
      languageState,
      jobRoleState,
      activityFilter,
      metricFilters: isSortApplied
        ? isSortedColumn
          ? metricFilterState
          : []
        : isAdvanceFilterColumn
        ? metricFilterState
        : [],
    },
    teamState,
    startDate || selected.startDate,
    endDate || selected.endDate,
    selected.sprintId,
    metricKey,
    isSortApplied
      ? isSortedColumn
        ? []
        : selectedContributors
      : isAdvanceFilterApplied
      ? isAdvanceFilterColumn
        ? []
        : selectedContributors
      : contributors,
    isAdvanceFilterApplied &&
      showContributorPercentiles &&
      ![
        'OVERDUE_TICKETS',
        'BLOCKED_TICKETS',
        'ACTIVE_WORKLOAD',
        'TOTAL_WORKLOAD',
        'TIME_TO_ZERO',
      ].includes(metricKey)
      ? (isSortApplied ? isSortedColumn : isAdvanceFilterColumn)
        ? []
        : filteredContributors
      : [],
    isSortedColumn || isAdvanceFilterColumnWithoutSort ? currentPage : 0,
    10,
    isSortedColumn ? orderBy : ''
  );

  useEffect(() => {
    if (
      data &&
      ((['asc', 'desc'].includes(orderBy) && isSortedColumn) ||
        isAdvanceFilterColumnWithoutSort)
    ) {
      const contributors = data.rows.map(
        (item: ContributorMetricRow) => item.contributor
      );
      const filteredContributors = data.filteredContributors;
      setTotalPages(data?.totalPages ?? 1);
      setSelectedContributors(contributors);
      setFilteredContributors(filteredContributors);
      setIsContributorsEmpty(contributors.length === 0);
    }
  }, [data]);

  useEffect(() => {
    if (orderColumn !== metricKey) {
      setOrderBy('');
    }
  }, [orderColumn]);

  const BORDER = '1px solid #E0E0E0';
  const isLastMetricColumn =
    selectedMetrics.length > 0 &&
    selectedMetrics[selectedMetrics.length - 1].metricKey === metricKey;

  const headerInner = (
    <Flex w="full" align="center" justify={'center'}>
      <Flex
        w="full"
        cursor={
          disableSort || DATA_POINT_KEYS.includes(metricKey)
            ? 'default'
            : 'pointer'
        }
        gap={1}
        align="center"
        onMouseEnter={() => !disableSort && setIsHover(true)}
        onMouseLeave={() => !disableSort && setIsHover(false)}
        onClick={() => {
          if (disableSort || DATA_POINT_KEYS.includes(metricKey)) {
            return;
          }

          const nextOrder =
            orderBy === '' ? 'asc' : orderBy === 'asc' ? 'desc' : '';

          setOrderBy(nextOrder);
          setOrderByUrl(nextOrder);

          if (nextOrder === '') {
            setOrderColumn('');
          } else {
            setOrderColumn(metricKey);
          }

          if (nextOrder === 'desc') {
            setCurrentPage(0);
          }

          scrollToTop();
          setIsHover(false);
        }}
      >
        {!disableSort &&
          orderColumn !== metricKey &&
          !DATA_POINT_KEYS.includes(metricKey) && (
            <Tooltip hasArrow label={`Sort by ${title}`} isOpen={isHover}>
              <Icon w={19} h={19}>
                <SortIcon
                  width={25}
                  height={25}
                  color={colors.text.secondary}
                />
              </Icon>
            </Tooltip>
          )}
        {!disableSort &&
          orderColumn === metricKey &&
          !DATA_POINT_KEYS.includes(metricKey) && (
            <Tooltip
              hasArrow
              label={
                orderBy === 'asc' ? `Sorted Ascending` : 'Sorted Descending'
              }
              isOpen={isHover}
            >
              <Icon w={19} h={19}>
                {orderBy === 'asc' ? (
                  <SortAsscending
                    width={25}
                    height={25}
                    color={colors.text.secondary}
                  />
                ) : (
                  <SortDescending
                    width={25}
                    height={25}
                    color={colors.text.secondary}
                  />
                )}
              </Icon>
            </Tooltip>
          )}
        <Text
          fontSize="sm"
          fontFamily="heading"
          fontWeight="medium"
          color="text.secondary2"
          w="fit-content"
          noOfLines={1}
          wordBreak="break-all"
        >
          {title === 'Acceptance Rate By Language' ? 'Acceptance Rate' : title}
        </Text>
      </Flex>
      {metricTooltip?.info && (
        <Tooltip label={metricTooltip?.info} hasArrow>
          <Box>
            <InfoIcon width={17} height={17} />
          </Box>
        </Tooltip>
      )}
    </Flex>
  );

  if (gridLayout) {
    const rowContributors = gridLayout.rowContributors;
    const showSkeleton = isLoading && (loading || contributors?.length > 0);
    const skeletonCount = Math.max(10, rowContributors.length);

    return (
      <>
        {showSkeleton
          ? [...Array(skeletonCount)].map((_, index) => (
              <GridItem
                key={`m-sk-${metricKey}-${index}`}
                gridRow={index + 2}
                gridColumn={gridLayout.columnStart}
                minH={rowHeight || '110px'}
                display="flex"
                alignItems="flex-start"
                borderLeft={BORDER}
                borderBottom={BORDER}
                borderRight={isLastMetricColumn ? BORDER : undefined}
                bg="white"
                p={3}
              >
                <Flex
                  align="flex-start"
                  mb={1}
                  w="full"
                  direction="column"
                  gap={2}
                >
                  <Flex align="flex-start" gap={2}>
                    <Box
                      as="span"
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg="gray.200"
                    />
                    <Box h="20px" w="60px" borderRadius="md" bg="gray.200" />
                  </Flex>
                  <Box h="24px" w="100%" borderRadius="md" bg="gray.200" />
                </Flex>
              </GridItem>
            ))
          : rowContributors.map((email, index) => {
              const row = data?.rows?.find(
                (r: ContributorMetricRow) => r.contributor === email
              );
              return (
                <GridItem
                  key={`${metricKey}-${email}-${index}`}
                  gridRow={index + 2}
                  gridColumn={gridLayout.columnStart}
                  minH={rowHeight || '110px'}
                  h="100%"
                  display="flex"
                  alignItems="stretch"
                  borderLeft={BORDER}
                  borderBottom={BORDER}
                  borderRight={isLastMetricColumn ? BORDER : undefined}
                  bg="white"
                  p={0}
                >
                  <TrendCell
                    showContributorPercentiles={showContributorPercentiles}
                    metricList={metricList}
                    data={row?.metricRowDataList || []}
                    author={email}
                    title={title}
                    compareTo={compareTo}
                    showRightBorder={false}
                    metric={metricKey}
                    moveToolTip={rowContributors.length - 1 === index}
                    rowHeight={rowHeight}
                    startDate={startDate}
                    endDate={endDate}
                    team={sidebarTeamId}
                    gridCell
                  />
                </GridItem>
              );
            })}
      </>
    );
  }

  return (
    <Flex
      direction="column"
      position="sticky"
      left={0}
      bg="white"
      h={'fit-content'}
      minW="220px"
      zIndex={80}
    >
      <Flex
        bg="#FBFBFB"
        position="sticky"
        top={0}
        borderLeft="1px solid #E0E0E0"
        borderRight={
          selectedMetrics[selectedMetrics.length - 1].metricKey === metricKey
            ? '1px solid #E0E0E0'
            : undefined
        }
        align="center"
        minH="52px"
        pl={4}
        pr={1}
        zIndex={50}
      >
        {headerInner}
      </Flex>

      <Divider />

      {isLoading && (loading || contributors?.length > 0)
        ? [...Array(10)].map((_, index) => (
            <MetricLoadingCell key={index} rowHeight={rowHeight} />
          ))
        : data?.rows?.map((row: ContributorMetricRow, index: number) => (
            <TrendCell
              showContributorPercentiles={showContributorPercentiles}
              metricList={metricList}
              data={row.metricRowDataList || []}
              author={row.contributor}
              title={title}
              key={`${row?.contributor}-${index}`}
              compareTo={compareTo}
              showRightBorder={
                selectedMetrics[selectedMetrics.length - 1].metricKey ===
                metricKey
              }
              metric={metricKey}
              moveToolTip={data?.rows?.length - 1 === index}
              rowHeight={rowHeight}
              startDate={startDate}
              endDate={endDate}
              team={sidebarTeamId}
            />
          ))}
    </Flex>
  );
};

export default MetricCol;
